import { CustomerSearch } from "@/features/customers/customer-search";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { UnifiedOrderRequest } from "@/api/unified-orders";
import { User, Package, FileText, Plus, Minus, Info } from "lucide-react";

interface OrderConfigStepProps {
  data: Partial<UnifiedOrderRequest>;
  onNext: (data: Partial<UnifiedOrderRequest>) => void;
}

const orderConfigSchema = z.object({
  placerCustomerId: z.string().optional(),
  referenceNumber: z.string().optional(),
  notes: z.string().optional(),
  shipmentCount: z
    .number()
    .min(1, "At least one shipment is required")
    .max(10, "Maximum 10 shipments allowed"),
});

export function OrderConfigStep({ data, onNext }: OrderConfigStepProps) {
  const form = useForm<z.infer<typeof orderConfigSchema>>({
    resolver: zodResolver(orderConfigSchema),
    defaultValues: {
      placerCustomerId: data.placerCustomerId ?? "none",
      referenceNumber:
        typeof data.metadata?.referenceNumber === "string"
          ? data.metadata.referenceNumber
          : "",
      notes:
        typeof data.metadata?.notes === "string" ? data.metadata.notes : "",
      shipmentCount: Math.max(1, data.shipments?.length ?? 0),
    },
  });

  const shipmentCount = form.watch("shipmentCount");

  const handleSubmit = (values: z.infer<typeof orderConfigSchema>) => {
    const metadata: Record<string, string> = {};

    if (values.referenceNumber) {
      metadata.referenceNumber = values.referenceNumber;
    }

    if (values.notes) {
      metadata.notes = values.notes;
    }

    // Keep configured shipment drafts when returning from the next step.
    const shipments = Array.from(
      { length: values.shipmentCount },
      (_, index) => data.shipments?.[index] ?? {}
    ) as UnifiedOrderRequest["shipments"];

    onNext({
      placerCustomerId:
        values.placerCustomerId === "none"
          ? undefined
          : values.placerCustomerId,
      metadata,
      shipments,
      _shipmentCount: values.shipmentCount,
    } as Parameters<typeof onNext>[0]);
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          id="order-config-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid gap-6 lg:grid-cols-2"
        >
          {/* Customer Section */}
          <div className="min-w-0 rounded-lg border bg-card p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-full bg-primary/10 p-2">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Customer Information</h3>
                <p className="text-sm text-muted-foreground">
                  Link this order to an existing customer account
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="placerCustomerId"
              render={({ field }) => {
                return (
                  <FormItem className="flex flex-col">
                    <FormLabel>Customer (Optional)</FormLabel>
                    <FormControl>
                      <CustomerSearch
                        value={field.value === "none" ? undefined : field.value}
                        onValueChange={(id) => field.onChange(id || "none")}
                        placeholder="Search customers..."
                        allowClear
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>

          {/* Order Details Section */}
          <div className="min-w-0 rounded-lg border bg-card p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-full bg-primary/10 p-2">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Order Details</h3>
                <p className="text-sm text-muted-foreground">
                  Add reference number and notes for this order
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <FormField
                control={form.control}
                name="referenceNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reference Number (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., PO-12345" {...field} />
                    </FormControl>
                    <FormDescription>
                      Internal reference or PO number
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Special instructions or notes..."
                        className="resize-none"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Internal notes for this order
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Shipment Count Section */}
          <div className="rounded-lg border bg-card p-4 sm:p-6 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-full bg-primary/10 p-2">
                <Package className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  Shipment Configuration
                </h3>
                <p className="text-sm text-muted-foreground">
                  Configure how many shipments this order will contain
                </p>
              </div>
            </div>

            <FormField
              control={form.control}
              name="shipmentCount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium">
                    Number of Shipments
                  </FormLabel>
                  <div className="bg-muted/30 rounded-lg border-2 border-dashed p-4 sm:p-6 mt-3">
                    <div className="flex items-center justify-center gap-2 sm:gap-4">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="size-10 sm:size-12"
                        onClick={() =>
                          field.onChange(Math.max(1, field.value - 1))
                        }
                        disabled={field.value <= 1}
                      >
                        <Minus className="h-5 w-5" />
                      </Button>

                      <div className="min-w-[72px] text-center sm:min-w-[120px]">
                        <div className="text-4xl font-bold text-primary">
                          {field.value}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {field.value === 1 ? "shipment" : "shipments"}
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="size-10 sm:size-12"
                        onClick={() =>
                          field.onChange(Math.min(10, field.value + 1))
                        }
                        disabled={field.value >= 10}
                      >
                        <Plus className="h-5 w-5" />
                      </Button>
                    </div>
                    <p className="text-sm text-center text-muted-foreground mt-3">
                      Split your order across multiple shipments (max 10)
                    </p>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Shipment Preview */}
          {shipmentCount > 1 && (
            <div className="rounded-lg border bg-card p-4 sm:p-6 lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="rounded-full bg-primary/10 p-2">
                  <Info className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Shipment Preview</h3>
                  <p className="text-sm text-muted-foreground">
                    You will configure each shipment in the next step
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: shipmentCount }, (_, index) => (
                  <Card key={index} className="border-dashed">
                    <CardContent className="p-4 text-center">
                      <Package className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                      <p className="font-medium">Shipment #{index + 1}</p>
                      <Badge variant="secondary" className="mt-2">
                        Not configured
                      </Badge>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </form>
      </Form>
    </div>
  );
}
